import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { Award, Download, Share2, ShieldCheck, Search, Sparkles, MessageSquareText, Clock } from "lucide-react";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { savePdf, sharePdf, openPdf, isNativeApp } from "@/lib/native-pdf";
import { PdfViewerModal } from "@/components/PdfViewerModal";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";

export default function Certificados() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [validateCode, setValidateCode] = useState("");
  const [showValidate, setShowValidate] = useState(false);
  const [surveyGate, setSurveyGate] = useState({ open: false, cert: null });
  const [viewerCert, setViewerCert] = useState(null); // <- estado do modal PDF viewer

  const load = () => {
    setLoading(true);
    api
      .get("/certificates/me")
      .then((r) => setItems(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const acknowledgeCert = async (certId) => {
    try {
      await api.post(`/certificates/${certId}/acknowledge`);
    } catch {
      /* silencioso — apenas remove da Home */
    }
  };

  // Formata a data de liberação (available_from, em UTC) no fuso de Brasília.
  const fmtReleaseDate = (iso) => {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
    } catch {
      return "";
    }
  };

  /**
   * Baixa e ABRE o certificado PDF com UX zero-fricção.
   *
   * # Estratégia por plataforma (ago/2026 v2.3.2 — em resposta a bug de
   *   popup blocker em produção que fazia 90% dos alunos travarem no fluxo)
   *
   * - **Web (desktop, mobile browser, PWA)**: abre um **modal in-app**
   *   (`PdfViewerModal`) que:
   *     1) Mostra spinner + "Emitindo seu certificado…" IMEDIATAMENTE.
   *     2) Faz fetch com barra de progresso (se Content-Length disponível).
   *     3) Renderiza o PDF INLINE via <iframe> (sem popup, sem bloqueio).
   *     4) Oferece botões nativos: Baixar, Compartilhar, Nova aba.
   *   ➜ NUNCA é bloqueado por popup — o modal já existe no DOM.
   *
   * - **App nativo (Capacitor iOS/Android)**: fluxo direto sem modal —
   *   usa `openPdf()` que abre o PDF em tela cheia no visualizador do SO
   *   (QuickLook / Intent.ACTION_VIEW). Mais rápido, mais nativo.
   *
   * - **Gate de pesquisa**: se backend retorna 403 (aluno não respondeu a
   *   pesquisa de satisfação), o modal fecha e abre o diálogo de pesquisa.
   */
  const performDownload = async (cert) => {
    // Native app (iOS/Android via Capacitor) — mantém o fluxo do FileViewer
    if (isNativeApp()) {
      try {
        const token = localStorage.getItem("ceap_token");
        const res = await fetch(`${API}/certificates/${cert.id}/download`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.status === 403) {
          setSurveyGate({ open: true, cert });
          load();
          return;
        }
        if (!res.ok) throw new Error("download failed");
        const blob = await res.blob();
        if (!blob || blob.size < 1024 || !(blob.type || "").includes("pdf")) {
          toast.error("O arquivo do certificado veio vazio. Contate o suporte.");
          return;
        }
        const filename = `certificado_${cert.validation_code}.pdf`;
        const result = await openPdf(blob, filename);
        if (!result?.canceled) {
          acknowledgeCert(cert.id);
        }
      } catch (err) {
        const msg = String(err?.message || err || "");
        if (/cancel/i.test(msg) || /aborted/i.test(msg)) return;
        toast.error("Falha ao baixar certificado");
        // eslint-disable-next-line no-console
        console.error("[performDownload native]", err);
      }
      return;
    }

    // Web (desktop / mobile browser / PWA) — abre o modal in-app.
    // O modal cuida do fetch (com progresso) e da visualização inline.
    setViewerCert(cert);
  };

  // Handler chamado pelo modal ao carregar o PDF com sucesso — marca cert como acknowledged
  const onViewerSuccess = () => {
    if (viewerCert) acknowledgeCert(viewerCert.id);
  };

  // Fetch function passada para o modal (respeita o survey gate)
  const buildFetchFn = (cert) => async (signal) => {
    const token = localStorage.getItem("ceap_token");
    const res = await fetch(`${API}/certificates/${cert.id}/download`, {
      headers: { Authorization: `Bearer ${token}` },
      signal,
    });
    // Se o backend retornar 403 (survey required), abre o diálogo de pesquisa
    if (res.status === 403) {
      setViewerCert(null);
      setSurveyGate({ open: true, cert });
      load();
    }
    return res;
  };

  const onDownload = (cert) => {
    // Certificado cancelado (revogado pelo Gestor Master)
    if (cert.is_cancelled) {
      toast.error("Este certificado foi cancelado e não está mais disponível.");
      return;
    }
    // Trava por data de liberação (end_date 08h / sexta 08h)
    if (cert.is_time_locked) {
      toast(`Seu certificado estará disponível a partir de ${fmtReleaseDate(cert.available_from)} às 08h.`, { duration: 6000 });
      return;
    }
    // Gate de pesquisa: se o curso exige pesquisa e ela ainda não foi respondida,
    // interrompe o download e abre o modal explicativo. O aluno é levado para
    // a pesquisa e retorna para clicar novamente.
    if (cert.survey_required && !cert.survey_completed) {
      setSurveyGate({ open: true, cert });
      return;
    }
    performDownload(cert);
  };

  const goToSurvey = () => {
    const cert = surveyGate.cert;
    setSurveyGate({ open: false, cert: null });
    if (cert?.course_id) {
      navigate(`/pesquisa-satisfacao/${cert.course_id}`);
    } else {
      toast.error("Curso não encontrado para responder à pesquisa");
    }
  };

  /**
   * Compartilha o certificado do aluno usando o Share Sheet nativo (Capacitor)
   * ou Web Share API (browser). Ver `/app/frontend/src/lib/native-pdf.js`.
   *
   * Estratégia:
   *  1) **Nativo (iOS/Android app)**: escreve PDF em Filesystem Cache e abre
   *     Share Sheet nativo — mesmo fluxo do "Baixar PDF" pois "salvar" e
   *     "compartilhar" ficam no mesmo menu do SO.
   *  2) **Web (browser)**: usa `navigator.share({files})` (Web Share API v2)
   *     para enviar o PDF direto pra WhatsApp/Gmail/Telegram/etc.
   *  3) **Fallback web**: copia link de validação (`/validar/{code}`).
   *
   * Também respeita o gate de pesquisa (mesma regra do "Baixar PDF").
   */
  const performShare = async (cert) => {
    try {
      const validationUrl = `${window.location.origin}/validar/${cert.validation_code}`;
      const shareText = `Meu certificado do curso "${cert.course_name}" (CEAP BRASIL). Valide em: ${validationUrl}`;

      // Baixa o PDF do backend (respeita o gate de pesquisa)
      const token = localStorage.getItem("ceap_token");
      const res = await fetch(`${API}/certificates/${cert.id}/download`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 403) {
        setSurveyGate({ open: true, cert });
        load();
        return;
      }

      if (res.ok) {
        const blob = await res.blob();
        if (blob && blob.size > 1024) {
          const filename = `certificado_${cert.validation_code}.pdf`;
          try {
            const result = await sharePdf(blob, filename, {
              title: "Meu Certificado CEAP",
              text: shareText,
            });
            if (!result?.canceled) {
              toast.success("Certificado compartilhado");
              acknowledgeCert(cert.id);
            }
            return;
          } catch (err) {
            // Cancelamento pelo usuário — silencioso.
            if (err?.name === "AbortError") return;
            const msg = String(err?.message || err || "");
            if (/cancel/i.test(msg) || /aborted/i.test(msg)) return;
            // Outros erros caem no fallback abaixo.
            // eslint-disable-next-line no-console
            console.warn("[performShare] sharePdf falhou, tentando fallback:", err);
          }
        }
      }

      // Fallback: Web Share API v1 (só texto + URL)
      if (typeof navigator.share === "function") {
        try {
          await navigator.share({
            title: "Certificado CEAP",
            text: cert.course_name,
            url: validationUrl,
          });
          return;
        } catch (err) {
          if (err?.name === "AbortError") return;
        }
      }

      // Último recurso: copia link
      const ok = await copyToClipboard(validationUrl);
      if (ok) toast.success("Link de validação copiado");
      else toast.error("Não foi possível compartilhar. Link: " + validationUrl);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error("[performShare]", err);
      toast.error("Falha ao compartilhar certificado");
    }
  };

  const onShare = (cert) => {
    if (cert.is_cancelled) {
      toast.error("Este certificado foi cancelado e não está mais disponível.");
      return;
    }
    if (cert.is_time_locked) {
      toast(`Seu certificado estará disponível a partir de ${fmtReleaseDate(cert.available_from)} às 08h.`, { duration: 6000 });
      return;
    }
    // Mesmo gate do download: se pesquisa pendente, interceptar antes.
    if (cert.survey_required && !cert.survey_completed) {
      setSurveyGate({ open: true, cert });
      return;
    }
    performShare(cert);
  };

  return (
    <div className="space-y-4" data-testid="certificados-page">
      <div className="flex items-center justify-between">
        <h1 className="font-display font-semibold text-xl text-[hsl(var(--primary))]">Meus certificados</h1>
        <Button variant="outline" size="sm" onClick={() => setShowValidate(true)} data-testid="open-validate-dialog">
          <ShieldCheck className="w-4 h-4 mr-1" /> Validar
        </Button>
      </div>
      {loading ? (
        <div className="space-y-2">
          <Card className="rounded-xl">
            <CardContent className="p-4">Carregando…</CardContent>
          </Card>
        </div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={Award}
          title="Nenhum certificado ainda"
          description="Conclua um curso e seu certificado oficial CEAP aparecerá aqui."
        />
      ) : (
        <div className="space-y-3" data-testid="certificates-list">
          {items.map((c) => {
            const needsSurvey = c.survey_required && !c.survey_completed;
            const cancelled = c.is_cancelled;
            const timeLocked = c.is_time_locked;
            const blocked = cancelled || timeLocked;
            return (
              <Card key={c.id} className="rounded-xl hover:shadow-md transition-shadow">
                <CardContent className="p-4">
                  <div className="flex items-start gap-3">
                    <div className="w-11 h-11 rounded-xl flex items-center justify-center bg-[hsl(var(--gold)/0.2)] text-[hsl(var(--gold-foreground))]">
                      <Award className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-display font-semibold text-[hsl(var(--primary))] line-clamp-2">
                        {c.course_name}
                      </h3>
                      <div className="flex flex-wrap gap-1.5 mt-1">
                        <Badge variant="secondary">{c.course_hours}h</Badge>
                        <Badge variant="outline">{c.course_city}</Badge>
                        {cancelled && (
                          <Badge
                            variant="outline"
                            className="border-rose-300 text-rose-800 bg-rose-50"
                            data-testid={`cert-cancelled-${c.id}`}
                          >
                            Cancelado
                          </Badge>
                        )}
                        {!cancelled && timeLocked && (
                          <Badge
                            variant="outline"
                            className="border-sky-300 text-sky-800 bg-sky-50"
                            data-testid={`cert-timelock-${c.id}`}
                          >
                            <Clock className="w-3 h-3 mr-1" /> Disponível em {fmtReleaseDate(c.available_from)} às 08h
                          </Badge>
                        )}
                        {!blocked && needsSurvey && (
                          <Badge
                            variant="outline"
                            className="border-amber-300 text-amber-800 bg-amber-50"
                            data-testid={`cert-pending-survey-${c.id}`}
                          >
                            <MessageSquareText className="w-3 h-3 mr-1" /> Pesquisa pendente
                          </Badge>
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">
                        {c.course_date} • {c.professor}
                      </p>
                      <p className="text-[11px] font-mono-id text-muted-foreground mt-1">
                        Código: {c.validation_code}
                      </p>
                    </div>
                  </div>
                  {cancelled ? (
                    <div className="mt-3 text-xs text-rose-800 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2" data-testid={`cert-cancelled-note-${c.id}`}>
                      Este certificado foi cancelado pelo Gestor Master. Em caso de dúvida, fale com a coordenação.
                    </div>
                  ) : timeLocked ? (
                    <div className="mt-3">
                      <Button size="sm" className="w-full" disabled data-testid={`certificate-locked-${c.id}`}>
                        <Clock className="w-4 h-4 mr-1" /> Disponível em {fmtReleaseDate(c.available_from)} às 08h
                      </Button>
                    </div>
                  ) : (
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        className="flex-1"
                        onClick={() => onDownload(c)}
                        data-testid={`certificate-download-${c.id}`}
                      >
                        <Download className="w-4 h-4 mr-1" /> Baixar PDF
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1"
                        onClick={() => onShare(c)}
                        data-testid={`certificate-share-${c.id}`}
                      >
                        <Share2 className="w-4 h-4 mr-1" /> Compartilhar
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      {/* Modal de gate de pesquisa — só aparece se aluno clicou em "Baixar PDF"
          de um certificado que exige pesquisa e ela ainda não foi respondida */}
      <Dialog
        open={surveyGate.open}
        onOpenChange={(open) => setSurveyGate({ open, cert: open ? surveyGate.cert : null })}
      >
        <DialogContent className="max-w-[440px] rounded-2xl" data-testid="survey-gate-dialog">
          <DialogHeader>
            <div className="w-12 h-12 rounded-xl bg-amber-100 text-amber-800 flex items-center justify-center mb-2">
              <Sparkles className="w-6 h-6" />
            </div>
            <DialogTitle className="font-display text-lg">
              Antes de emitir seu certificado
            </DialogTitle>
            <DialogDescription className="text-sm leading-relaxed">
              Para baixar ou compartilhar seu certificado, precisamos que você responda uma <strong>rápida Pesquisa de Satisfação</strong> deste curso
              (leva cerca de 2 minutos). Sua opinião é fundamental para melhorarmos os próximos cursos do
              CEAP.
            </DialogDescription>
          </DialogHeader>
          {surveyGate.cert && (
            <div className="px-3 py-2 rounded-lg bg-[hsl(var(--muted))]/40 border border-[hsl(var(--border))]">
              <p className="text-xs text-muted-foreground">Curso</p>
              <p className="text-sm font-medium text-[hsl(var(--primary))] line-clamp-2">
                {surveyGate.cert.course_name}
              </p>
            </div>
          )}
          <DialogFooter className="flex-col sm:flex-row gap-2 mt-1">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => setSurveyGate({ open: false, cert: null })}
              data-testid="survey-gate-cancel"
            >
              Agora não
            </Button>
            <Button
              className="flex-1 bg-amber-600 hover:bg-amber-700"
              onClick={goToSurvey}
              data-testid="survey-gate-go"
            >
              <Sparkles className="w-4 h-4 mr-1" /> Responder pesquisa
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={showValidate} onOpenChange={setShowValidate}>
        <DialogContent className="max-w-[440px] rounded-2xl">
          <DialogHeader>
            <DialogTitle>Validar certificado</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">Digite o código de validação ou escaneie o QR.</p>
          <div className="flex gap-2 mt-2">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input
                className="pl-9"
                placeholder="Ex: ABC123XYZ"
                value={validateCode}
                onChange={(e) => setValidateCode(e.target.value.toUpperCase())}
                data-testid="validate-code-input"
              />
            </div>
            <Link to={`/validar/${validateCode}`}>
              <Button disabled={!validateCode} data-testid="validate-go-button">
                Validar
              </Button>
            </Link>
          </div>
        </DialogContent>
      </Dialog>

      {/* PDF Viewer Modal — usado no fluxo WEB (mobile/desktop/PWA).
          Substitui window.open que era bloqueado por popup. */}
      <PdfViewerModal
        open={!!viewerCert}
        onClose={() => setViewerCert(null)}
        title={viewerCert ? `Certificado — ${viewerCert.course_name}` : ""}
        courseName={viewerCert?.course_name}
        filename={viewerCert ? `certificado_${viewerCert.validation_code}.pdf` : "certificado.pdf"}
        validationUrl={viewerCert ? `${window.location.origin}/validar/${viewerCert.validation_code}` : undefined}
        fetchFn={viewerCert ? buildFetchFn(viewerCert) : null}
        onSuccess={onViewerSuccess}
      />
    </div>
  );
}
